import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import { DatePicker } from "antd";
import dayjs from "dayjs";
import 'dayjs/locale/en';
import logUserAction from "./utils/logUserAction";

const ReceiptModal = ({ order, onClose }) => {
  if (!order) return null;

  const groupedItems = [];

  order.items.forEach((item) => {
    if (item.name === 'Cookie Box') {
      groupedItems.push({ ...item, extras: [], isBox: true });
    } else if (item.name.startsWith('(Box)')) {
      const lastBox = groupedItems.slice().reverse().find(i => i.isBox && i.extras);
      if (lastBox) {
        lastBox.extras.push({ ...item, isCookie: true });
      }
    } else {
      groupedItems.push({ ...item });
    }
  });

  // Now group identical boxes
  const finalItems = [];

  groupedItems.forEach(item => {
    if (item.isBox) {
      const extrasKey = item.extras
        .map(e => `${e.name.replace('(Box) ', '')}:${e.quantity || 1}`)
        .sort()
        .join('|');

      const existing = finalItems.find(
        i => i.name === item.name && i.price === item.price && i.extrasKey === extrasKey
      );

      if (existing) {
        existing.quantity += 1;
      } else {
        finalItems.push({ ...item, quantity: 1, extrasKey });
      }
    } else {
      const extrasKey = item.extras
        ?.map(e => `${e.name}:${e.quantity}`)
        .sort()
        .join('|') || '';

      const existing = finalItems.find(
        i => i.name === item.name && i.price === item.price && i.extrasKey === extrasKey
      );

      if (existing) {
        existing.quantity += item.quantity || 1;
      } else {
        finalItems.push({ ...item, quantity: item.quantity || 1, extrasKey });
      }
    }
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-600 hover:text-black text-xl">&times;</button>

        <h3 className="text-lg font-semibold mb-2">Order #{order.orderID}</h3>
        <p><strong>User:</strong> {order.userEmail}</p>
        <p><strong>Store:</strong> {order.store}</p>
        <div className="mt-2">
          <strong>Payment:</strong>
          <ul className="mt-1">
            {order.paymentMethods?.map((p, i) => (
              <li key={i} className="flex items-center gap-2 text-sm mt-1">
                <img src={`/icons/${p.method}.png`} alt={p.method} className="h-5 w-5 object-contain" />
                <span>${parseFloat(p.amount).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>

        <h4 className="mt-4 font-semibold">Items:</h4>
        <ul className="mt-2 list-disc ml-5 text-sm">
          {finalItems.map((item, i) => (
            <li key={i}>
              {item.quantity}x {item.name} @ ${parseFloat(item.price).toFixed(2)}
              {item.extras?.length > 0 && (
                <ul className="ml-4 list-disc text-xs text-gray-600">
                  {item.extras.map((extra, j) => (
                    <li key={j}>
                      {extra.isCookie
                        ? `🍪 ${extra.quantity || 1}x ${extra.name.replace('(Box) ', '')}`
                        : `+ ${extra.quantity}x ${extra.name} ($${(extra.price * extra.quantity).toFixed(2)})`}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        <div className="mt-4 border-t pt-3 text-sm space-y-1">
          <p><strong>Subtotal:</strong> ${parseFloat(order.subtotal).toFixed(2)}</p>
          <p><strong>GST:</strong> ${parseFloat(order.gst).toFixed(2)}</p>
          <p><strong>QST:</strong> ${parseFloat(order.qst).toFixed(2)}</p>
          <p className="font-semibold"><strong>Total:</strong> ${parseFloat(order.grandTotal).toFixed(2)}</p>
        </div>
      </div>
    </div>
  );
};


const SearchOrderPage = () => {
  const [orderID, setOrderID] = useState('');
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [delayDone, setDelayDone] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [ordersByDate, setOrdersByDate] = useState([]);
  const [userStore, setUserStore] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const { RangePicker } = DatePicker;
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const LoadingSpinner = () => (
    <div className="fixed inset-0 flex justify-center items-center bg-white z-50">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-500"></div>
    </div>
  );

  useEffect(() => {
    const fetchUserStore = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const docSnap = await getDoc(doc(db, "users", user.uid));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserStore(data.store || '');
        setIsAdmin(data.admin || false);
      }
    };

    fetchUserStore();
  }, []);

  const fetchOrder = async () => {
    setError('');
    setOrder(null);
    setOrdersByDate([]);
    setLoading(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("You must be logged in.");
        setLoading(false);
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        setError("User profile not found.");
        setLoading(false);
        return;
      }

      const userData = userSnap.data();
      const isAdmin = userData.admin === true;
      const userStore = userData.store;

      const q = isAdmin
        ? query(collection(db, "orders"), where("orderID", "==", parseInt(orderID)))
        : query(
          collection(db, "orders"),
          where("orderID", "==", parseInt(orderID)),
          where("store", "==", userStore)
        );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("No order found.");
        setLoading(false);
        return;
      }

      const orderDoc = querySnapshot.docs[0];
      const orderData = orderDoc.data();

      if (isAdmin || orderData.store === userStore) {
        setOrder({ id: orderDoc.id, ...orderData });
        setHasSearched(true); // 👈 Hide search after success

        await logUserAction(currentUser, "Searched Order", {
          searchedOrderID: orderID,
          result: orderData.orderID || "Not Found",
          store: orderData.store || "Unknown"
        });
      } else {
        setError("You don't have permission to view this order.");
      }
    } catch (err) {
      console.error("Error fetching orders by date:", err.message, err.stack);
      setError("Something went wrong.");
    }

    setLoading(false);
  };

  const fetchOrdersByDate = async () => {
    setLoading(true);
    setError('');
    setOrdersByDate([]);
    setOrder(null);

    if (!startDate || !endDate) {
      setError("Please select a valid date range.");
      setLoading(false);
      return;
    }

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const q = isAdmin
        ? query(collection(db, "orders"), where("timestamp", ">=", start), where("timestamp", "<=", end))
        : query(collection(db, "orders"), where("store", "==", userStore), where("timestamp", ">=", start), where("timestamp", "<=", end));

      const querySnapshot = await getDocs(q);

      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      results.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setOrdersByDate(results);
      setHasSearched(true);

      await logUserAction(auth.currentUser, "Searched Orders by Date", {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        results: results.length
      });

    } catch (err) {
      console.error("Error fetching orders by date:", err.message, err.stack);
      setError("Something went wrong.");
    }

    setLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => setDelayDone(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (!delayDone) {
    return <LoadingSpinner />;
  }

  const resetSearch = () => {
    setOrderID('');
    setStartDate(null);
    setEndDate(null);
    setOrder(null);
    setOrdersByDate([]);
    setHasSearched(false);
    setError('');
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-12">
      <div className="w-full max-w-lg bg-gray-100 p-6 rounded-xl shadow-xl border">
        {!hasSearched && (
          <>
            <h2 className="text-xl font-bold mb-4 text-center">Search Order by Order ID</h2>

            <div className="mb-6 flex flex-col gap-4 text-left">
              <div className="flex gap-2">
                <input
                  type="number"
                  value={orderID}
                  onChange={(e) => setOrderID(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') fetchOrder();
                  }}
                  className="flex-1 border px-4 py-2 rounded"
                  placeholder="Enter Order ID"
                />
                <button
                  onClick={fetchOrder}
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Search
                </button>
              </div>

              <div className="flex items-center gap-4 my-2">
                <hr className="flex-1 border-gray-300" />
                <span className="text-gray-500 font-medium text-sm whitespace-nowrap">or</span>
                <hr className="flex-1 border-gray-300" />
              </div>

              <div>
                <h2 className="text-xl font-bold mb-4 text-center">Select Date Range</h2>
                <RangePicker
                  onChange={(dates) => {
                    if (!dates || dates.length !== 2) {
                      setStartDate(null);
                      setEndDate(null);
                    } else {
                      const [start, end] = dates;
                      setStartDate(dayjs(start).startOf("day").toDate());
                      setEndDate(dayjs(end).endOf("day").toDate());
                    }
                  }}
                  allowClear
                  format="YYYY-MM-DD"
                  value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
                  style={{ width: "100%" }}
                />
              </div>

              <button
                onClick={fetchOrdersByDate}
                className="bg-green-500 text-white px-4 py-2 rounded w-full"
              >
                Search by Date
              </button>
            </div>
          </>
        )}

        {hasSearched && (
          <div className="mb-4">
            <div className="text-center mb-4">
              <button
                onClick={resetSearch}
                className="inline-block px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 transition duration-200 shadow"
              >
                Search Again
              </button>
            </div>

            {order && (
              <div className="mt-6 border p-4 bg-white rounded shadow text-left">
                <h3 className="text-lg font-semibold mb-2">Order #{order.orderID}</h3>
                <p><strong>User:</strong> {order.userEmail}</p>
                <p><strong>Store:</strong> {order.store}</p>
                <div className="mt-2">
                  <strong>Payment:</strong>
                  <ul className="mt-1">
                    {order.paymentMethods.map((p, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm mt-1">
                        <img
                          src={`/icons/${p.method}.png`}
                          alt={p.method}
                          className="h-5 w-5 object-contain"
                        />
                        <span> ${parseFloat(p.amount).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <h4 className="mt-4 font-semibold">Items:</h4>

                <div className="mt-4 border-t pt-3 text-sm space-y-1">
                  <p><strong>Subtotal:</strong> ${parseFloat(order.subtotal).toFixed(2)}</p>
                  <p><strong>GST:</strong> ${parseFloat(order.gst).toFixed(2)}</p>
                  <p><strong>QST:</strong> ${parseFloat(order.qst).toFixed(2)}</p>
                  <p className="text-base font-semibold"><strong>Total:</strong> ${parseFloat(order.grandTotal).toFixed(2)}</p>
                </div>
              </div>
            )}

            {ordersByDate.length > 0 && (
              <div className="mt-6">
                <h4 className="text-center text-lg font-semibold mb-6">
                  Orders Found: {ordersByDate.length}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {ordersByDate
                    .filter(o => o.id !== order?.id) // ✅ filter out the one already shown
                    .map(order => (
                      <div
                        key={order.id}
                        className="border p-5 rounded-lg bg-white shadow-md hover:shadow-lg transition duration-200"
                      >
                        <p><strong>Order #{order.orderID}</strong></p>
                        <p><strong>User:</strong> {order.userEmail}</p>
                        <p><strong>Store:</strong> {order.store}</p>
                        <p><strong>Date:</strong> {new Date(order.timestamp.toDate()).toLocaleString()}</p>
                        <p><strong>Total:</strong> ${parseFloat(order.grandTotal).toFixed(2)}</p>

                        <div className="mt-3 w-full flex justify-center">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 transition duration-200 shadow"
                          >
                            Details
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

          </div>
        )}

        {loading && (
          <div className="flex justify-center mt-4">
            <div className="border-4 border-blue-500 border-t-transparent rounded-full w-8 h-8 animate-spin"></div>
          </div>
        )}

        {error && <p className="text-red-500 mt-2">{error}</p>}

      </div>
      <ReceiptModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />

    </div>
  );
};

export default SearchOrderPage;
